import { Camera, Image as ImageIcon, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

type ImageUploadProps = {
    onChange: (file: File | null) => void;
    savedImage?: string;
};

export default function ImageUpload({ onChange, savedImage }: ImageUploadProps) {
    const [image, setImage] = useState<File | null>(null);
    const [preview, setPreview] = useState<string | null>(savedImage ?? null);

    const cameraInputRef = useRef<HTMLInputElement>(null);
    const galleryInputRef = useRef<HTMLInputElement>(null);

    const handleImage = (file?: File) => {
        if (!file) return;

        // Optional validation
        if (!file.type.startsWith("image/")) {
            alert("Please select an image.");
            return;
        }

        setImage(file);
        onChange?.(file);

        const previewUrl = URL.createObjectURL(file);
        setPreview(previewUrl);
    };

    const removeImage = () => {
        setImage(null);
        setPreview(null);

        if (cameraInputRef.current) {
            cameraInputRef.current.value = "";
        }

        if (galleryInputRef.current) {
            galleryInputRef.current.value = "";
        }

        onChange?.(null);
    };

    useEffect(() => {
        return () => {
            if (preview) {
                URL.revokeObjectURL(preview);
            }
        };
    }, [preview]);

    return (
        <div className="w-full max-w-sm">
            {/* Camera */}
            <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => handleImage(e.target.files?.[0])}
            />

            {/* Gallery */}
            <input
                ref={galleryInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleImage(e.target.files?.[0])}
            />

            {preview ? (
                <div className="space-y-4">
                    <div className="group relative overflow-hidden rounded-xl border dark:border-primary-dark-border_active bg-gray-50">
                        <img
                            src={preview}
                            alt="Selected"
                            className="aspect-square w-full object-cover"
                        />

                        <button
                            type="button"
                            onClick={removeImage}
                            className="absolute hidden right-3 top-3 group-hover:flex h-9 w-9 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80"
                        >
                            <X size={18} />
                        </button>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <button
                            type="button"
                            onClick={() => cameraInputRef.current?.click()}
                            className="flex items-center justify-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium hover:bg-gray-50 dark:text-text-dark-secondary"
                        >
                            <Camera size={18} />
                            Retake
                        </button>

                        <button
                            type="button"
                            onClick={() => galleryInputRef.current?.click()}
                            className="flex items-center justify-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium hover:bg-gray-50 dark:text-text-dark-secondary"
                        >
                            <ImageIcon size={18} />
                            Change
                        </button>
                    </div>
                </div>
            ) : (
                <div className="rounded-xl border border-dashed border-gray-300 p-6">
                    <div className="mb-5 text-center">
                        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                            <ImageIcon size={22} className="text-gray-600" />
                        </div>

                        <p className="font-medium text-gray-900">Add photo</p>

                        <p className="mt-1 text-sm text-gray-500 dark:text-text-dark-secondary">
                            Take a new photo or choose one from your gallery.
                        </p>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <button
                            type="button"
                            onClick={() => cameraInputRef.current?.click()}
                            className="flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700"
                        >
                            <Camera size={18} />
                            Take Photo
                        </button>

                        <button
                            type="button"
                            onClick={() => galleryInputRef.current?.click()}
                            className="flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-text-dark-primary hover:dark:text-text-dark-surface hover:dark:border-primary-dark-border_active hover:dark:bg-primary-dark-secondary hover:bg-gray-50"
                        >
                            <ImageIcon size={18} />
                            Gallery
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}